import { useCallback, useMemo, useRef, useState } from 'react';

import { useChartContext } from '@/components/Chart/context/ChartContext';
import type { ExtendedSeriesOptionsType } from '@/components/Chart/types/highcharts';

import useFullScreenOptions from '../hooks/options/useFullScreenOptions';

import type { HighchartsReactRefObject } from '@highcharts/react/Stock';

const useFullScreenChart = ({
  chartSeries,
  isDataHub,
  isLightMode
}: {
  chartSeries: ExtendedSeriesOptionsType[] | undefined;
  isDataHub?: boolean;
  isLightMode: boolean;
}) => {
  const [seriesHidden, setSeriesHidden] = useState<string[]>(() => []);
  const chartComponentRef = useRef<HighchartsReactRefObject>(null);
  const chartHeight = window.innerHeight - (isDataHub ? 460 : 260);
  const { createOptions } = useFullScreenOptions({ height: chartHeight, isDataHub, isLightMode });

  const options = useMemo(
    () => chartSeries && createOptions(chartSeries, seriesHidden),
    [chartSeries, createOptions, seriesHidden]
  );

  const toggleSeriesVisibility = useCallback((id: string) => {
    setSeriesHidden(prevState => {
      const isHidden = prevState.includes(id);
      if (isHidden) {
        return prevState.filter(item => item !== id);
      }
      return [...prevState, id];
    });
  }, []);

  const { setChartSeries, chartRef } = useChartContext();

  const handleChartRef = useCallback(
    (instance: HighchartsReactRefObject | null) => {
      chartComponentRef.current = instance;
      if (instance) {
        setChartSeries(chartSeries);
        chartRef.current = instance;
      }
    },
    [chartRef, chartSeries, setChartSeries]
  );

  return { options, chartComponentRef, handleChartRef, toggleSeriesVisibility };
};

export default useFullScreenChart;
